import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Copy, Check, Sparkles, CreditCard as Edit } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { WALLS } from '../types/lanesAndDrivers';
import { supabase } from '../lib/supabase';

interface ProfileData {
  core_value_1: string | null;
  core_value_2: string | null;
  core_value_3: string | null;
  lane_1: string | null;
  lane_2: string | null;
  lane_3: string | null;
  lane_4: string | null;
  value_1_description: string | null;
  value_2_description: string | null;
  value_3_description: string | null;
  lane_1_description: string | null;
  lane_2_description: string | null;
  lane_3_description: string | null;
  lane_4_description: string | null;
}

export function AISparkPrompt() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      setProfile(data);
    }
  }

  function getWallName(wallId: string | null): string {
    if (!wallId) return '';
    return WALLS.find(w => w.id === wallId)?.name || wallId;
  }

  function generatePrompt(): string {
    if (!profile) return '';

    const value1 = profile.core_value_1 || 'Value 1';
    const value2 = profile.core_value_2 || 'Value 2';
    const value3 = profile.core_value_3 || 'Value 3';

    const wall1 = getWallName(profile.lane_1) || 'Wall 1';
    const wall2 = getWallName(profile.lane_2) || 'Wall 2';
    const wall3 = getWallName(profile.lane_3) || 'Wall 3';
    const wall4 = getWallName(profile.lane_4) || 'Wall 4';

    const value1Desc = profile.value_1_description || `my personal definition of ${value1}`;
    const value2Desc = profile.value_2_description || `my personal definition of ${value2}`;
    const value3Desc = profile.value_3_description || `my personal definition of ${value3}`;

    const wall1Desc = profile.lane_1_description || `my vision for ${wall1}`;
    const wall2Desc = profile.lane_2_description || `my vision for ${wall2}`;
    const wall3Desc = profile.lane_3_description || `my vision for ${wall3}`;
    const wall4Desc = profile.lane_4_description || `my vision for ${wall4}`;

    return `Your core values are ${value1}, ${value2}, and ${value3}.

${value1} means ${value1Desc}.
${value2} means ${value2Desc}.
${value3} means ${value3Desc}.

Your walls and mission are to help build your foundation in ${wall1}, ${wall2}, ${wall3}, and ${wall4}.

${wall1} is ${wall1Desc}.
${wall2} is ${wall2Desc}.
${wall3} is ${wall3Desc}.
${wall4} is ${wall4Desc}.

______

REPLY INSTRUCTIONS:
Use this framework to solve problems and achieve goals. When a user presents a challenge, analyze which Wall needs strengthening. Provide a response that is daring, clear, and focused on immediate action. Use everyday language. No dashes. No icons.

IF THE USER APPEARS STUCK
Ask them if they would like to go through the Alignment Engine. If they agree, ask them the 6 questions below in order. Between each question, reply with a 1-2 sentence acknowledgement of their answer, and then ask the next question. Once they answer the 6th question, acknowledge their response and ask if they need help with that.

1. What can you celebrate about yesterday?
2. What did you learn from that?
3. What is your pressing goal?
4. What is the fear that comes up when you think of that goal?
5. What do your core values (${value1}, ${value2}, and ${value3}) say to do regarding that goal? Brainstorm a list of ideas.
6. From that list, what action can you take today?`;
  }

  async function handleCopy() {
    const prompt = generatePrompt();
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy to clipboard. Please try selecting and copying manually.');
    }
  }

  if (!profile) {
    return (
      <ProtectedLayout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </ProtectedLayout>
    );
  }

  const prompt = generatePrompt();

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-4 animate-pulse">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Your AI Prompt
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Here's your personalized prompt to use with any AI assistant. Copy this and paste it into
              ChatGPT, Claude, or your favorite AI tool to get values-aligned guidance.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Your Custom Prompt</h2>
              <Button
                onClick={handleCopy}
                variant="secondary"
                className="flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy to Clipboard
                  </>
                )}
              </Button>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                {prompt}
              </pre>
            </div>

            <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
              <Button
                onClick={() => navigate('/clarify-definitions')}
                variant="secondary"
                size="sm"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Definitions
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-bold text-blue-900 mb-3">How to Use Your Spark Prompt</h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-800">
              <li>Copy the prompt above using the button</li>
              <li>Open your preferred AI assistant (ChatGPT, Claude, etc.)</li>
              <li>Paste the prompt to start a new conversation</li>
              <li>Ask questions or share challenges to get values-aligned guidance</li>
              <li>Use the six alignment questions whenever you feel stuck</li>
            </ol>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Setup Complete!
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              You've completed your onboarding. Now you can start using the app to track your
              growth, journal your progress, and work toward your goals with values-aligned action.
            </p>
            <Button
              onClick={() => navigate('/dashgrowthloop')}
              size="lg"
              className="px-8"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
